import {
  Box,
  Button,
  Stack,
  Tooltip,
  Typography,
  alpha,
  useTheme
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import Grid from '@mui/material/Grid'
import BoxWithBorder from 'components/atoms/container/BoxWithBorder'
import InCardStack from 'components/atoms/container/InCardStack'
import colors from 'styles/colors'
import RowStack from 'components/atoms/container/RowStack'
import { formatDate, formatTime } from 'utils/formatDateTime'
import moment from 'moment'
import { getLocalUser } from 'utils/localStorage'
import ProfileAvatar from 'components/atoms/ProfileAvatar'
import BoxWithBackground from 'components/atoms/container/BoxWithBackground'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import { ReactComponent as MedalGold } from 'assets/images/medalGold.svg'
import { ReactComponent as MedalSilver } from 'assets/images/medalSilver.svg'
import { ReactComponent as MedalBronze } from 'assets/images/medalBronze.svg'
import { ReactComponent as SocialLeaderboard } from 'assets/images/socialLeaderboard.svg'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import { IconContainer } from 'components/atoms/Icons'
import { useNavigate } from 'react-router-dom'
import { JOURNEY } from 'routes/constants'

const indexedColors = [
  alpha(colors.Gold, 0.1),
  alpha(colors.Silver, 0.1),
  alpha(colors.Bronze, 0.1)
]

const RankingItem = ({ item, index, ownerIndex, lastFive, allZero }) => {
  const { t } = useTranslation()
  const isOwner = index === ownerIndex
  const fontWeight = isOwner ? 600 : 400
  const completed = item.completePercent === 100
  const backgroundColor = allZero
    ? colors.MutedTeal50
    : index <= 2
      ? indexedColors[index]
      : isOwner
        ? lastFive && !completed
          ? alpha(colors.ErrorRed, 0.1)
          : colors.MutedTeal50
        : colors.MetalGrey50
  return (
    <BoxWithBackground
      backgroundColor={backgroundColor}
      width='100%'
      paddingY={4}
      paddingX={8}
      borderRadius={2}>
      <RowStack justifyContent='space-between'>
        <RowStack width='unset' spacing={3}>
          <Typography width={20} fontWeight={fontWeight}>
            {item.completePercent === 0 ? '--' : index + 1}
          </Typography>
          <ProfileAvatar user={item.user} size={32} />
          <Typography fontWeight={fontWeight}>
            {isOwner
              ? t('common.youCap')
              : `${item.user.firstName} ${item.user.lastName.substring(0, 1)}.`}
          </Typography>
        </RowStack>
        {completed ? (
          <IconContainer
            icon={
              index === 0 ? (
                <MedalGold />
              ) : index === 1 ? (
                <MedalSilver />
              ) : index === 2 ? (
                <MedalBronze />
              ) : (
                <CheckCircleIcon />
              )
            }
            style={{ color: colors.MidnightTeal300 }}
          />
        ) : (
          <Typography
            fontWeight={fontWeight}
            color={isOwner && lastFive ? 'error' : 'primary'}>
            {item.completePercent?.toFixed(0)}%
          </Typography>
        )}
      </RowStack>
    </BoxWithBackground>
  )
}

const MoreVertItem = () => (
  <Box flexGrow={1} display='flex' alignItems='center'>
    <MoreVertIcon sx={{ color: colors.LightGrey200 }} />
  </Box>
)
const CohortLeaderboard = ({ cohortRanking, futureSession }) => {
  const { t, i18n } = useTranslation()
  const theme = useTheme()
  const navigate = useNavigate()
  const user = getLocalUser()
  const timestamp = formatTime(moment())
  const facitlitationCompleted = moment
    .utc(cohortRanking?.facilitationDate)
    .isBefore()
  const rankings = cohortRanking?.rankings || []
  const allZero = rankings.every(item => item.completePercent === 0)
  const displayList = allZero
    ? rankings.sort((a, b) => {
        if (a.user.id === user.id) return -1 // move to top
        if (b.user.id === user.id) return 1 // move to top
        return a.user.firstName.localeCompare(b.user.firstName) // alphabetical order
      })
    : rankings
  const ownerIndex = displayList.findIndex(item => item.user?.id === user.id)
  const ownerLastFive = ownerIndex > 4 && rankings.length - ownerIndex < 6
  const owerBehind =
    !allZero &&
    displayList[ownerIndex].completePercent === 0 &&
    !facitlitationCompleted
  return (
    <Grid width={365}>
      <BoxWithBorder
        padding={8}
        borderWidth={1}
        borderRadius={3}
        borderColor={colors.LightGrey200}
        position='relative'
        boxShadow='0px 1px 4px 0px rgba(0, 0, 0, 0.20)'
        height='100%'>
        <InCardStack height={350} width={333} overflow='hidden' spacing={4}>
          <RowStack justifyContent='space-between'>
            <Stack padding={0}>
              <RowStack width='unset' spacing={2}>
                <Typography variant='h4'>
                  {t('home.cohortLeaderboard')}
                </Typography>
                <Tooltip
                  title={t('home.cohortLeaderboardTooltip', {
                    count: cohortRanking?.rankings?.length
                  })}>
                  <InfoOutlinedIcon fontSize='small' color='primary' />
                </Tooltip>
              </RowStack>
              <Typography variant='body1Micro' color='textSecondary'>
                {facitlitationCompleted
                  ? formatDate(
                      cohortRanking?.facilitationDate,
                      i18n.resolvedLanguage
                    )
                  : t('home.cohortLeaderboardTimestamp', { time: timestamp })}
              </Typography>
            </Stack>
            <Typography
              variant='body1Micro'
              color='textSecondary'
              maxWidth={100}
              textAlign='right'>
              {t('home.milestoneCompletion')}
            </Typography>
          </RowStack>
          <InCardStack spacing={2} width='100%' height='100%'>
            {!futureSession && cohortRanking && (
              <>
                {displayList
                  ?.slice(0, ownerIndex < 4 ? 4 : 3)
                  .map((item, index) => (
                    <RankingItem
                      key={`ranking-item-${index}`}
                      item={item}
                      index={index}
                      ownerIndex={ownerIndex}
                      allZero={allZero}
                    />
                  ))}
                {ownerIndex >= 4 && (
                  <>
                    <MoreVertItem />
                    <RankingItem
                      item={displayList[ownerIndex]}
                      index={ownerIndex}
                      ownerIndex={ownerIndex}
                      allZero={allZero}
                      lastFive={ownerLastFive}
                    />
                  </>
                )}
                {ownerIndex < rankings.length - 1 && <MoreVertItem />}
              </>
            )}
          </InCardStack>
        </InCardStack>
        {(futureSession || owerBehind) && (
          <BoxWithBackground
            backgroundColor={
              futureSession
                ? alpha(theme.palette.primary.main, 0.8)
                : alpha(theme.palette.background.paper, 0.6)
            }
            sx={{
              position: 'absolute',
              height: '100%',
              width: '100%',
              top: 0,
              left: 0,
              borderRadius: 3,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backdropFilter: 'blur(2.5px)'
            }}>
            <BoxWithBackground
              backgroundColor={theme.palette.background.paper}
              width={300}
              boxShadow='0px 1px 30px rgba(0, 0, 0, 0.15)'>
              <InCardStack padding={12} spacing={futureSession ? 4 : 6}>
                {futureSession ? (
                  <>
                    <IconContainer icon={<SocialLeaderboard />} size={56} />
                    <Typography variant='h4'>
                      {t('home.cohortLeaderboard')}
                    </Typography>
                    <Typography
                      variant='body1KindaSmall'
                      textAlign={'center'}
                      color='primary'>
                      {t('home.cohortLeaderboardFutureDesc')}
                    </Typography>
                  </>
                ) : (
                  <>
                    <Typography variant='h4'>
                      {t('home.dontFallBehind')}
                    </Typography>
                    <Typography>{t('home.dontFallBehindDesc')}</Typography>
                    <Box />
                    <Button size='small' onClick={() => navigate(JOURNEY)}>
                      {t('home.jumpBackIn')}
                    </Button>
                  </>
                )}
              </InCardStack>
            </BoxWithBackground>
          </BoxWithBackground>
        )}
      </BoxWithBorder>
    </Grid>
  )
}

export default CohortLeaderboard
